import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Accordion, AccordionSummary, AccordionDetails, Toolbar, Button, Card, CardContent, Dialog, DialogContent, ButtonBase, DialogActions, Typography } from '@mui/material';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import { ExpandMore } from '@mui/icons-material';
import ExtendedField from '../../components/text_field';
import SearchBox from '../../components/searchbox';
import axios from 'axios';
import Config from '../../config';
import SelectField from '../../components/suggest_field';
import ConfirmDialog from '../../components/confirm_dialog';
import DownloadButton from '../../components/download_button';
import Image from '../../components/image';
import { connectToShopify } from '../oauth/shopify';
import { connectToChannelAdvisor } from '../oauth/channeladvisor';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/search.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

const identity = (v: any) => (v);

const methodMapping: Record<string, Record<string, string>> = {
    "Accepts": {
        "PostOrders": "orders",
        "PostShipments": "shipments",
        "PostAcknowledgements": "acknowledgements",
        "PostInvoices": "invoices",
        "PostReturns": "returns",
        "PostInventory": "inventory",
        "PostProduct": "product catalog"
    },
    "Provides": {
        "GetOrders": "orders",
        "GetAcknowledgements": "acknowledgements",
        "GetShipments": "shipments",
        "GetInvoices": "invoices",
        "GetReturns": "returns",
        "GetInventory": "inventory",
        "GetFiles": "other files",
        "CreateLabel": "shipping labels",
        "GetCatalog": "product catalog"
    }
}

const getMethodDescription = (methods: string[]) => {
    let ret = "";
    Object.keys(methodMapping).forEach(r => {
        let methodNames = Object.keys(methodMapping[r]).filter(x => methods.indexOf(x) > -1).map(x => methodMapping[r][x]);
        if(methodNames.length > 0){
            let methodList = methodNames.length > 1 ? methodNames.slice(0, -1).join(', ') + ' and ' + methodNames.slice(-1) : methodNames[0];
            ret += (ret ? " " : "") + r + " " + methodList + ".";
        }
    });
    return ret;
}

type ConnectorField = {
    key: string;
    label: string;
    name: string;
    component: any;
    options?: { label: string; value: string }[];
    type?: string;
    inputProps?: any;
    placeholder?: any;
    section?: string;
};

type ConnectorAction = {
    onClick: () => void;
    text: string;
    disabled?: boolean;
    confirm?: string;
};

type Connector = {
    name: string;
    fields: ConnectorField[];
    methods: string;
    workflowConfigured?: boolean;
    properties: Record<string, any>;
    actions: (values: any) => ConnectorAction[];
};

type SystemEditorProps = {
    connector: Connector;
    showConfirm: (message: string, action: () => void) => void;
    wrapper?: any;
    actionWrapper?: any;
    saveSettings: (values: any) => void;
    actionVariant?: 'text' | 'outlined' | 'contained';
    actionWrapperStyle?: React.CSSProperties;
};

const SystemEditor = (props: SystemEditorProps) => {
    let {connector, showConfirm, wrapper, actionWrapper, saveSettings, actionVariant, actionWrapperStyle} = props;
    let sections = connector.fields.map(r => r.section);
    sections = sections.filter((r, i) => sections.every((x, j) => x !== r || i <= j));
    let Wrapper: any = wrapper || "div";
    let ActionWrapper: any = actionWrapper || "div";
    return <Form onSubmit={(e: any) => saveSettings({...e, System: connector.name})}
    initialValues={connector.properties}
    render={({ handleSubmit, pristine, invalid, values }: any) => (
      <form autoComplete='off' onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', flex: '1 1 auto'}}>
      <Wrapper>
      {connector.methods ? <div style={{fontSize: '18px', textAlign: 'left', marginBottom: '10px'}}>{connector.methods}</div> : ''}
      {sections.map(s => <div key={s} style={{marginBottom: '10px'}}>
          {s && <div style={{fontSize: '18px', textAlign: 'left'}}>{s}</div>}
          {connector.fields.filter(r => r.section === s).map(field => <div key={field.name} style={{display: 'flex', flex: '1 1 auto', position: 'relative'}}>
              <Field style={{width: '100%'}} format={(v: any) => v ? v.trim() : v} formatOnBlur parse={identity} autoComplete='one-time-code' {...field} inputProps={{...field.inputProps, autoComplete: 'off'}}/>
          </div>)}
      </div>)}
      </Wrapper>
      <ActionWrapper style={actionWrapperStyle}>
      {connector.actions && connector.actions(values).map((action: ConnectorAction, a: number) =>
          <DownloadButton key={a} variant='outlined' style={{marginRight: '1em'}} onClick={() => !action.confirm ? action.onClick() : showConfirm(action.confirm!, action.onClick)} disabled={action.disabled}>{action.text}</DownloadButton>
      )}
        <Button variant={actionVariant} color='primary' type='submit' disabled={pristine || invalid}>Save</Button>
      </ActionWrapper>
      </form>)}/>;
}

type ConnectionsProps = {
    setTitle?: (title: string) => void;
    isBillable?: boolean;
};

const Connections = (props: ConnectionsProps) => {
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [confirmed, setConfirmed] = useState<boolean>(false);
    const [connectors, setConnectors] = useState<Connector[]>([]);
    const [showNewSystem, setShowNewSystem] = useState<boolean>(false);
    const [showActionResult, setShowActionResult] = useState<boolean>(false);
    const [confirmMessage, setConfirmMessage] = useState<string | undefined>(undefined);
    const [confirmAction, setConfirmAction] = useState<(() => void) | undefined>(undefined);
    const [newSystem, setNewSystem] = useState<Connector | null | undefined>(undefined);
    const [term, setTerm] = useState<string | null | undefined>(undefined);
    const [actionResult, setActionResult] = useState<any>(undefined);

    const saveSettings = useCallback(async (e: any) => {
        Object.keys(e).forEach(k => {
            if(e[k] === undefined){
                e[k] = null;
            }
        });
        await axios.put(Config.api + `/odata/Company/Functions.SetSystemSettings`, e, {params: {system: e.System}});
        await loadConnectorsRef.current();
        setShowNewSystem(false);
    }, []);

    const runAction = useCallback(async (system: string, name: string, e: any) => {
        Object.keys(e).forEach(k => {
            if(e[k] === undefined){
                e[k] = null;
            }
        });
        let data = (await axios.post(Config.api + `/odata/Company/Functions.ExecuteConnectorAction`, e, {params: {system: system, actionName: name}})).data;
        setActionResult(data);
        setShowActionResult(true);
    }, []);

    const specialConnectors = useMemo<Record<string, { name: string; actions: (values: any) => ConnectorAction[] }>>(() => ({
        ChannelAdvisor: {
            name: "ChannelAdvisor",
            actions: (values: any) => {
                var actions: ConnectorAction[] = [{onClick: () => connectToChannelAdvisor(values), disabled: !values.ChannelAdvisorClientId, text:  values.ChannelAdvisorRefreshToken ? 'Reconnect' : 'Connect' }];
                if(values.ChannelAdvisorRefreshToken){
                    actions.push({onClick: () => {values.ChannelAdvisorRefreshToken = null; saveSettings({...values, System: "ChannelAdvisor"})}, text: 'Disconnect', confirm: "Are you sure you want to disconnect from ChannelAdvisor?"});
                }
                return actions;
            }
        },
        Shopify: {
            name: "Shopify",
            actions: (values: any) => {
                var actions: ConnectorAction[] = [{onClick: () => connectToShopify(values), disabled: !values.ShopifyStoreName, text:  values.ShopifyAccessToken ? 'Reconnect' : 'Connect' }];
                if(values.ShopifyAccessToken){
                    actions.push({onClick: () => {values.ShopifyAccessToken = null; saveSettings({...values, System : "Shopify"})}, text: 'Disconnect', confirm: "Are you sure you want to disconnect from Shopify?"});
                }
                return actions;
            }
        },
    }), [saveSettings]);

    const loadConnectors = useCallback(async () => {
        let res = (await axios.get(Config.api + "/odata/Company/Functions.GetAllSystemSettings")).data.value;
        let loaded: Connector[] = res.map((r: any) => ({
            name: r.System,
            fields: r.Settings.filter((r: any) => !r.IndirectEditable).map((r: any) => {
                let prop: ConnectorField = { key: r.Name, label: r.FriendlyName, name: r.Name, component: ExtendedField };
                if(r.Options && r.Options.length > 0){
                    prop.component = SelectField;
                    prop.options = r.LabelledOptions.map((x: any) => ({label: x.Label, value: x.Value}));
                } else {
                    prop.component = ExtendedField;
                }
                if(r.Secret){
                    prop.type = "password";
                    prop.inputProps = {
                        onFocus: (e: any) => {if(e.currentTarget.type === "password"){e.currentTarget.type = "text";}},
                        onBlur: (e: any) => {e.currentTarget.type = "password";}
                    };
                }
                prop.placeholder = r.DefaultValue;
                prop.section = r.Section;

                return prop;
            }),
            methods: getMethodDescription(r.Methods),
            workflowConfigured: r.WorkflowConfigured,
            properties: r.Settings.reduce(function(acc: any, cur: any, i: number) {
              acc[cur.Name] = cur.Value;
              return acc;
          }, {}),
            actions: (values: any) => {
                let actions: ConnectorAction[] = [];
                if(r.OAuthName){
                    actions.push({onClick: async () => {await saveSettings({...values, System: r.System}); window.location.href = `/oauth/${r.OAuthName}`}, text:  r.IsConnected ? 'Reconnect' : 'Connect' });
                    if(r.IsConnected){
                        actions.push({onClick: async () => {
                            await axios.get(Config.api + `/odata/Company/Functions.DisconnectOAuth?system=${r.OAuthName}`);
                            await loadConnectorsRef.current();
                        }, text: 'Disconnect', confirm: `Are you sure you want to disconnect from ${r.System}?`});
                    }
                } else if(specialConnectors[r.System] && specialConnectors[r.System].actions){
                    actions = actions.concat(specialConnectors[r.System].actions(values));
                }
                if(r.Actions){
                    for(var i = 0; i < r.Actions.length; i++){
                        let actionName = r.Actions[i].Name;
                        let friendlyName = r.Actions[i].FriendlyName;
                        actions.push({onClick: () => runAction(r.System, actionName, values), text: friendlyName});
                    }
                }
                return actions;
            }
        }));
        setConnectors(loaded);
    }, [specialConnectors, saveSettings, runAction]);

    // Keep a stable ref to the latest loadConnectors so callbacks that need to
    // re-fetch (saveSettings, OAuth disconnect) can call it without creating a
    // dependency cycle with useCallback.
    const loadConnectorsRef = useRef(loadConnectors);
    loadConnectorsRef.current = loadConnectors;

    // Pre-existing (unused in master too); kept as-is to preserve behavior parity.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const connectToQuickBooksOnline = (e: any) => {
        saveSettings(e);
        var clientId = 'Q00a6dkixdsSkyJd3hDikIbp3X8PaOiQwPYvQX2SFwRlNUMsKh';
        if(e.QBOBaseUrl === 'https://sandbox-quickbooks.api.intuit.com'){
            clientId = 'Q0wtAGRrfgSLIWeJdEuonYZnZgrDIWnyE1k758MfWpljbxN1aB';
        }
        var redirect = `${window.location.protocol}//${window.location.host}/oauth/quickbooks`;
        window.location.href = `https://appcenter.intuit.com/connect/oauth2?client_id=${clientId}&response_type=code&scope=com.intuit.quickbooks.accounting&redirect_uri=${redirect}&state=0`;
    }

    const updateTerm = async (e: React.SyntheticEvent<HTMLInputElement>) => {
        let value = e.currentTarget.value;
        setTerm(prev => prev !== value ? value : prev);
    }

    const handleShowConfirm = (message: string, action: () => void) => {
        setShowConfirm(true);
        setConfirmMessage(message);
        setConfirmAction(() => action);
    }

    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Connectors');
        }
        loadConnectorsRef.current();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let configuredConnectors = connectors.filter(r => r.workflowConfigured || Object.values(r.properties).filter(r => r != null).length > 0);
    if(!props.isBillable && connectors.length > 0 && configuredConnectors.length === 0){
        return (
            <><Grid container spacing={2}>
                <Grid size={12}>
                    <Card>
                        <CardContent style={{textAlign: 'left'}}>
                            <div>You can configure settings for various connectors from this page. Additional configuration is required within Logicbroker to initialize connections so please contact <a href='mailto:sales@logicbroker.com'>sales</a> if you would like to connect to any of the systems on this page.</div>
                            <div style={{marginTop: '20px'}}>
                                <SearchBox handleKeyUp={updateTerm} handleChange={updateTerm} placeholder={`Search connections`}/>
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
                </Grid>
                <Grid container spacing={2} style={{paddingRight: '1em', paddingLeft: '1em'}}>
                {connectors.filter(r => (!term || r.name.toLowerCase().indexOf(term.toLowerCase()) > -1) && Object.values(r.properties).filter(r => r != null).length === 0).map(r =>
                    <Grid size={{ sm: 3, md: 3, lg: 3 }} key={r.name} style={{position: 'relative', overflow: 'hidden', padding: '5px'}}>
                        <div style={{width: '100%'}}><Card style={{height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', padding: '10px'}}>
                            <Image style={{maxWidth: '100%', maxHeight: '100%'}} title={r.name} src={`/images/connections/${r.name.toLowerCase().replace(/ /g, '')}.png`} altImage={<div>{r.name}</div>}/>
                        </Card></div>
                    </Grid>)}
                </Grid></>
        );
    }
    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Card>
                    <CardContent style={{textAlign: 'left'}}>
                        <div>You can configure settings for various connectors from this page. Additional configuration is required within Logicbroker to initialize connections so please contact <a href='mailto:support@logicbroker.com'>support</a> first when connecting to a new system.</div>
                        <div style={{marginTop: '10px'}}><Button variant='outlined' onClick={() => {setShowNewSystem(true); setNewSystem(null); setTerm(null);}}>Connect to a new system</Button></div>
                        </CardContent>
                </Card>
            </Grid>
            {configuredConnectors.map(con =>
                <Grid key={con.name} size={12}>
                    <Card>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Toolbar className='lbtoolbar' >{con.name}</Toolbar>
                            </AccordionSummary>
                            <AccordionDetails>
                                <SystemEditor connector={con} showConfirm={handleShowConfirm} saveSettings={saveSettings} actionVariant='contained' actionWrapperStyle={{textAlign: 'left', marginTop: '1em'}}/>
                            </AccordionDetails>
                        </Accordion>
                    </Card>
                </Grid>
            )}
            <ConfirmDialog open={showConfirm} onClose={() => setShowConfirm(false)} message={confirmMessage} onConfirm={() => {setShowConfirm(false); confirmAction!()}}/>
            {/* `width` isn't part of MUI's DialogProps (superseded by maxWidth) but was
                already a no-op prop pre-migration; preserved via cast rather than
                silently dropped (same pattern as other stale-MUI-prop spots). */}
            <Dialog open={showNewSystem} fullWidth onClose={() => setShowNewSystem(false)} {...({width: "md"} as any)}>
                <Toolbar className='lbtoolbar' >{newSystem ? `Connect to ${newSystem.name}` : 'Add Connection'}</Toolbar>
                {newSystem && <>
                    <SystemEditor connector={newSystem} showConfirm={handleShowConfirm} saveSettings={saveSettings} wrapper={DialogContent} actionWrapper={DialogActions} actionVariant='contained'/>
                </>}
                {!newSystem && <DialogContent style={{overflowY: 'hidden'}}>
                    <div style={{marginBottom: '20px', width: '100%'}} >
                        <SearchBox handleKeyUp={updateTerm} handleChange={updateTerm} placeholder={`Search connections`}/>
                    </div>
                    <Grid container spacing={2} style={{maxHeight: '400px', overflowY: 'auto'}}>
                    {connectors.filter(r => (!term || r.name.toLowerCase().indexOf(term.toLowerCase()) > -1) && Object.values(r.properties).filter(r => r != null).length === 0).map(r =>
                        <Grid size={{ sm: 4, md: 4, lg: 4 }} key={r.name} style={{position: 'relative', overflow: 'hidden', padding: '5px'}} onClick={props.isBillable ? () => setNewSystem(r) : undefined}>
                            <ButtonBase style={{width: '100%'}}><Card style={{width: '100%', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', padding: '10px'}}>
                                <Image style={{maxWidth: '100%', maxHeight: '100%'}} title={r.name} src={`/images/connections/${r.name.toLowerCase().replace(/ /g, '')}.png`} altImage={<div>{r.name}</div>}/>
                            </Card></ButtonBase>
                        </Grid>)}
                    </Grid>
                </DialogContent>}
            </Dialog>
            <Dialog open={showActionResult} fullWidth onClose={() => setShowActionResult(false)} {...({width: "sm"} as any)}>
                <Toolbar className='lbtoolbar' >Results</Toolbar>
                <DialogContent>
                    <Typography style={{whiteSpace: 'pre-wrap'}}>{actionResult}</Typography>
                </DialogContent>
            </Dialog>
        </Grid>
    );
}

export default Connections;
